import { useLayoutEffect, useState } from 'react';

/**
 * Hook to scale a button element to fit within its container.
 * Uses ResizeObserver to automatically recalculate when container or button sizes change.
 *
 * @param containerRef - Ref to the container element
 * @param buttonRef - Ref to the button element
 * @param padding - Padding to subtract from container dimensions (default: 8)
 * @param dependencies - Array of dependencies that should trigger recalculation.
 *
 * @returns The scale factor (0-1) to apply to the button
 */
export const useButtonScale = (
  containerRef: React.RefObject<HTMLElement | null>,
  buttonRef: React.RefObject<HTMLElement | null>,
  padding: number = 8,
  dependencies: React.DependencyList = [],
): number => {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const button = buttonRef.current;
    if (!container || !button) return;

    const compute = () => {
      // Force a reflow to ensure styles are applied before measuring
      void button.offsetHeight;

      const computedStyle = window.getComputedStyle(button);
      const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
      const borderRight = parseFloat(computedStyle.borderRightWidth) || 0;
      const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
      const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;

      const contentW = button.scrollWidth || 1;
      const contentH = button.scrollHeight || 1;
      const buttonW = contentW + borderLeft + borderRight;
      const buttonH = contentH + borderTop + borderBottom;

      // Subtract padding to keep a safe margin
      const containerW = Math.max(1, (container.clientWidth || 1) - padding * 2);
      const containerH = Math.max(1, (container.clientHeight || 1) - padding * 2);
      const next = Math.min(containerW / buttonW, containerH / buttonH, 1);
      // Avoid tiny re-renders due to float noise
      if (!Number.isFinite(next)) return;
      setScale((prev) => (Math.abs(prev - next) > 0.002 ? next : prev));
    };

    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      // Use double RAF to ensure styles are fully applied before measuring
      raf = requestAnimationFrame(() => {
        requestAnimationFrame(compute);
      });
    };

    // Observe container size changes
    const roContainer = new ResizeObserver(schedule);
    roContainer.observe(container);

    // Observe button size changes (when styles update)
    const roButton = new ResizeObserver(schedule);
    roButton.observe(button);

    // Initial measure with double RAF to ensure styles are applied
    requestAnimationFrame(() => {
      requestAnimationFrame(compute);
    });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      roContainer.disconnect();
      roButton.disconnect();
    };
  }, [containerRef, buttonRef, padding, ...dependencies]);

  return scale;
};
